import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import FeatureFaq from '@/components/marketing/features/FeatureFaq'
import StackExplorer from '@/components/marketing/technology/StackExplorer'
import TechBreadcrumbs from '@/components/marketing/technology/TechBreadcrumbs'
import TechCta from '@/components/marketing/technology/TechCta'
import TechSubnav from '@/components/marketing/technology/TechSubnav'
import type { TechnologyCapabilitiesProps } from '@/types/technology'

/* /technology/capabilities — the full catalogue (audience-first plan,
 * 2026-10-18). The reference behind the audience pages, for a buyer who wants
 * the whole list before they talk to us.
 *
 * Two things moved here from the hub: the StackExplorer (every module, grouped
 * by the page that owns it, filterable by status) and the roadmap horizons.
 * Every card carries its detail and points folded in a native <details>, and
 * its id is the module key — an audience page's chips for the modules it owns
 * link to /technology/capabilities#<key>, so they land on that card.
 *
 * Nothing here is wrapped in a ScrollReveal: the page is long, a reader
 * arriving on an anchor lands mid-page, and the card they were sent to must be
 * visible without waiting for an animation.
 */
export default function Capabilities({
  hero,
  answer,
  faq,
  breadcrumbs,
  stack,
  status_legend,
  roadmap,
  subnav,
  brand,
  cta,
}: TechnologyCapabilitiesProps) {
  return (
    <>
      <AtmosphericHero minHeight="40vh">
        <div className="mx-auto max-w-3xl text-center">
          <TechBreadcrumbs crumbs={breadcrumbs} />
          <p className="marketing-eyebrow-mono justify-center">{hero.eyebrow}</p>
          <h1 className="hero-headline mt-6" style={{ fontSize: 'clamp(2.1rem, 4.6vw, 3.6rem)' }}>
            {hero.title}
          </h1>
          <p className="marketing-lead mx-auto mt-7 max-w-[65ch]">{hero.body}</p>
        </div>
      </AtmosphericHero>

      <TechSubnav items={subnav} brand={brand} />

      <section className="container mx-auto px-6 pt-14 lg:pt-16">
        <p className="marketing-lead mx-auto max-w-3xl">{answer}</p>
        <nav aria-label="Sections on this page" className="mx-auto mt-8 max-w-3xl">
          <ul className="flex flex-wrap gap-2">
            {[
              { href: '#modules', label: 'Every capability' },
              { href: '#roadmap', label: 'Where this is going' },
            ].map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="marketing-chip inline-flex items-center gap-2 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wavbee-orange)]"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </section>

      <section
        id="modules"
        aria-labelledby="modules-heading"
        className="container mx-auto scroll-mt-52 sm:scroll-mt-44 px-6 py-16 lg:py-20"
      >
        <div className="mx-auto max-w-6xl">
          <h2
            id="modules-heading"
            className="marketing-section-title"
            style={{ fontSize: 'clamp(1.6rem, 3.2vw, 2.4rem)' }}
          >
            Every capability, by the page that covers it
          </h2>
          <p className="mt-4 max-w-[65ch] text-[0.95rem] leading-[1.75] text-white/60">
            Live means it runs in wavbee&rsquo;s own distribution business today. Open any card for
            how it works.
          </p>
          <div className="mt-10">
            <StackExplorer groups={stack} legend={status_legend} />
          </div>
        </div>
      </section>

      <section
        id="roadmap"
        aria-labelledby="roadmap-heading"
        className="container mx-auto scroll-mt-52 sm:scroll-mt-44 px-6 pb-16 lg:pb-20"
      >
        <div className="mx-auto max-w-5xl">
          <h2
            id="roadmap-heading"
            className="marketing-section-title"
            style={{ fontSize: 'clamp(1.6rem, 3.2vw, 2.4rem)' }}
          >
            Where this is going
          </h2>
          <p className="mt-4 max-w-[65ch] text-[0.95rem] leading-[1.75] text-white/60">
            Horizons rather than dates, because a date published here becomes a promise nobody
            agreed to.
          </p>
          <ol className="mt-10 flex flex-col gap-5">
            {roadmap.map((horizon) => (
              <li
                key={horizon.key}
                className="rounded-[1.5rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-6 sm:p-8"
              >
                <p className="marketing-eyebrow-mono">
                  {horizon.horizon}
                </p>
                <h3
                  className="mt-3 text-lg font-semibold leading-snug text-white sm:text-xl"
                  style={{ fontFamily: 'var(--wavbee-font-display)' }}
                >
                  {horizon.title}
                </h3>
                <p className="mt-3 max-w-[65ch] text-[0.9rem] leading-[1.8] text-white/60">{horizon.body}</p>
                {horizon.items.length > 0 && (
                  <ul className="mt-5 grid gap-2.5 border-t border-white/[0.07] pt-5 sm:grid-cols-2">
                    {horizon.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5">
                        <span className="mt-[0.5rem] h-1 w-1 shrink-0 rounded-full bg-[#ff6a1a]" aria-hidden="true" />
                        <span className="text-[0.8125rem] leading-[1.7] text-white/60">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {faq.length > 0 && <FeatureFaq faq={faq} />}

      <TechCta cta={cta} />
    </>
  )
}
